import { Component, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiService } from 'src/app/shared/services/api.service';
import { ToastrMsgService } from 'src/app/shared/services/shared-services/toastr.service';
import { ViewProfileService } from 'src/app/shared/services/view-profile-servies/view-profile.service';
import { UserService } from 'src/app/shared/services/user-services/user.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-personal-details',
  templateUrl: './personal-details.component.html',
  styleUrls: ['./personal-details.component.css'],
})
export class PersonalDetailsComponent implements OnInit {
  errorMessage: string = '';
  profileMenu: any = true;
  states: string[] = [];
  districtOptions: string[] = [];
  cityOptions: string[] = [];
  myForm: FormGroup = new FormGroup({
    org_logo: new FormControl(''),
    name: new FormControl('', Validators.required),
    email: new FormControl('', [Validators.required, Validators.email]),
    image: new FormControl(''),
    mobile: new FormControl('', Validators.required),
    address: new FormControl('', Validators.required),
    state: new FormControl('', Validators.required),
    district: new FormControl('', Validators.required),
    city: new FormControl('', Validators.required),
    postal_code: new FormControl('', [Validators.pattern('[0-9]{6}')]),
    country: new FormControl('', Validators.required),
  });
  file: any;
  folderName: any = 'customer';

  profileData: any;
  imageData: any;
  imageurl: any;
  customerId: any;
  imageBaseUrl = environment.imageBaseUrl;
  constructor(
    private apiService: ApiService,
    private profileService: ViewProfileService,
    private userService: UserService,
    private toastrMsgService: ToastrMsgService,
    private readonly router: Router,
    private route: ActivatedRoute,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.loadStates();
    this.route.paramMap.subscribe((pm) => {
      const id = pm.get('id');
      if (id) {
        this.customerId = id;
        this.getDetails(id);
      } else {
        this.profileService.getProfile().subscribe((data: any) => {
          this.applyProfilePayload(data);
        });
      }
    });
  }

  private asLocArray(res: unknown): string[] {
    return Array.isArray(res) ? (res as string[]) : [];
  }

  private parseMeta(m: any): Record<string, any> {
    if (m == null) return {};
    if (typeof m === 'string') {
      try {
        return JSON.parse(m) as Record<string, any>;
      } catch {
        return {};
      }
    }
    return typeof m === 'object' ? (m as Record<string, any>) : {};
  }

  private loadStates(): void {
    this.apiService.getstates().subscribe({
      next: (res) => {
        this.states = this.asLocArray(res);
      },
      error: (err) => {
        console.error(err);
        this.toastrMsgService.error(
          err.error?.errorMessage || err.error?.message || this.translate.instant('PROFILE_ERR_LOAD_STATES'),
        );
      },
    });
  }

  /** Restore state → district → city from API data (district in metadata; legacy: city held district name). */
  private hydrateLocationFromData(data: any): void {
    const state = data?.state || '';
    const meta = this.parseMeta(data?.metadata);
    let district = meta['district'] || '';
    const savedCity = data?.city || '';
    this.myForm.patchValue({ state, district: '', city: '' });
    this.districtOptions = [];
    this.cityOptions = [];
    if (!state) {
      return;
    }
    this.apiService.getdistricts(state).subscribe({
      next: (dres) => {
        this.districtOptions = this.asLocArray(dres);
        if (!district && savedCity && this.districtOptions.includes(savedCity)) {
          district = savedCity;
        }
        this.myForm.patchValue({ district });
        if (!district) {
          return;
        }
        this.apiService.getSubdistricts(state, district).subscribe({
          next: (cres) => {
            this.cityOptions = this.asLocArray(cres);
            const cityVal =
              meta['district'] && savedCity && this.cityOptions.includes(savedCity)
                ? savedCity
                : !meta['district'] && district === savedCity
                  ? ''
                  : this.cityOptions.includes(savedCity)
                    ? savedCity
                    : '';
            this.myForm.patchValue({ city: cityVal });
          },
          error: (err) => {
            console.error(err);
            this.toastrMsgService.error(
              err.error?.errorMessage || err.error?.message || this.translate.instant('PROFILE_ERR_LOAD_CITIES'),
            );
          },
        });
      },
      error: (err) => {
        console.error(err);
        this.toastrMsgService.error(
          err.error?.errorMessage || err.error?.message || this.translate.instant('PROFILE_ERR_LOAD_DISTRICTS'),
        );
      },
    });
  }

  private resolveAccountType(data: any): 'organization' | 'staff' | 'user' | 'branch' {
    const explicit = data?.profile_account_type;
    if (explicit === 'staff' || explicit === 'user' || explicit === 'branch' || explicit === 'organization') {
      return explicit;
    }
    const jwtType = this.userService.getJwtAccountType();
    if (jwtType === 3) return 'staff';
    if (jwtType === 4) return 'user';
    if (jwtType === 5) return 'branch';
    if (jwtType === 1 || jwtType === 2) return 'organization';
    if (data?.email_id != null || data?.employee_code) return 'staff';
    if (data?.branch_name != null && data?.branch_id != null && !data?.employee_code) return 'branch';
    return 'organization';
  }

  private applyProfilePayload(data: any): void {
    this.profileData = data;
    const accountType = this.resolveAccountType(data);

    if (accountType === 'staff') {
      this.customerId = data.id ?? this.userService.getJwtUserId();
      this.myForm?.controls['name'].setValue(data.full_name || '');
      this.myForm?.controls['email'].setValue(data.email_id || data.contact_email || '');
      this.myForm?.controls['image'].setValue(data.profile_picture || data.image || '');
      this.myForm?.controls['mobile'].setValue(data.contact_phone || '');
      this.myForm?.controls['country'].setValue(data?.country || '');
      this.myForm?.controls['postal_code'].setValue(data.postal_code || '');
      this.myForm?.controls['address'].setValue(data.address || '');
      this.hydrateLocationFromData(data);
      const avatar = data.profile_picture || data.image || data.org_logo;
      this.imageurl = avatar ? this.imageBaseUrl + avatar : '';
      return;
    }

    if (accountType === 'user') {
      this.customerId = data.id ?? this.userService.getJwtUserId();
      this.myForm?.controls['name'].setValue(data.full_name || '');
      this.myForm?.controls['email'].setValue(data.email || '');
      this.myForm?.controls['image'].setValue(data.profile_picture || '');
      this.myForm?.controls['mobile'].setValue(data.primary_phone || data.mobile_number || '');
      this.myForm?.controls['country'].setValue(data?.country || '');
      this.myForm?.controls['postal_code'].setValue(data.postal_code || '');
      this.myForm?.controls['address'].setValue(data.address || '');
      this.hydrateLocationFromData(data);
      this.imageurl = data.profile_picture ? this.imageBaseUrl + data.profile_picture : '';
      return;
    }

    if (accountType === 'branch') {
      this.customerId = data.branch_id ?? data.id;
      this.myForm?.controls['name'].setValue(data.branch_name || data.primary_contact_name || '');
      this.myForm?.controls['email'].setValue(data.primary_contact_email || data.contact_email || '');
      this.myForm?.controls['image'].setValue(data.branch_logo || data.org_logo || '');
      this.myForm?.controls['mobile'].setValue(data.primary_contact_phone || data.contact_phone || '');
      this.myForm?.controls['country'].setValue(data?.country || '');
      this.myForm?.controls['postal_code'].setValue(data.postal_code || '');
      this.myForm?.controls['address'].setValue(data.address || '');
      this.hydrateLocationFromData(data);
      const avatar = data.branch_logo || data.org_logo;
      this.imageurl = avatar ? this.imageBaseUrl + avatar : '';
      return;
    }

    // Organization account
    this.customerId = data.org_id ?? data.id;
    this.myForm?.controls['name'].setValue(data.primary_contact_name || data.organization_name || '');
    this.myForm?.controls['email'].setValue(data.primary_contact_email || data.contact_email || '');
    this.myForm?.controls['image'].setValue(data.org_logo || '');
    this.myForm?.controls['mobile'].setValue(data.primary_contact_phone || data.contact_phone || '');
    this.myForm?.controls['country'].setValue(data?.country || '');
    this.myForm?.controls['postal_code'].setValue(data.postal_code || '');
    this.myForm?.controls['address'].setValue(data.address || '');
    this.hydrateLocationFromData(data);
    this.imageurl = data.org_logo ? this.imageBaseUrl + data.org_logo : '';
  }

  getDetails(id: any): void {
    this.profileService.getProfileById(id).subscribe((data) => {
      this.applyProfilePayload(data);
    });
  }

  handleFileInput(event: any) {
    this.file = event.target.files[0];

    if (!this.file) {
      this.errorMessage = this.translate.instant('PROFILE_ERR_NO_FILE');
      return;
    }

    const fileSize = this.file.size / 1024 / 1024;
    const fileType = this.file.type;

    if (fileType !== 'image/jpeg' && fileType !== 'image/png' && fileType !== 'image/jpg') {
      this.errorMessage = this.translate.instant('PROFILE_ERR_FILE_TYPE');
      this.file = null;
      return;
    }

    if (fileSize > 3) {
      this.errorMessage = this.translate.instant('PROFILE_ERR_FILE_SIZE');
      this.file = null;
      return;
    }

    this.errorMessage = '';
    this.imageurl = URL.createObjectURL(this.file);
    this.uploadImage(this.file);
  }

  uploadImage(file: any) {
    if (file) {
      this.apiService.uploadContractDocs(file, 'org')?.subscribe({
        next: (res: any) => {
          this.myForm.patchValue({
            org_logo: res?.org,
          });

          this.myForm.get('org_logo')?.updateValueAndValidity();
          this.toastrMsgService.success(this.translate.instant('PROFILE_TOAST_LOGO_UPLOADED'));
        },
        error: (err) => console.error('Organization logo upload failed', err),
      });
    }
  }

  updateAdmin() {
    const formValues = this.myForm.value;
    const accountType = this.resolveAccountType(this.profileData);
    const profileId = this.customerId ?? this.userService.getJwtUserId();

    let payload: Record<string, unknown>;
    if (accountType === 'staff') {
      payload = {
        full_name: formValues.name ?? '',
        email_id: formValues.email ?? '',
        contact_email: formValues.email ?? '',
        contact_phone: formValues.mobile ?? '',
        address: formValues.address ?? '',
        state: formValues.state ?? '',
        city: formValues.city ?? '',
        district: formValues.district ?? '',
        country: formValues.country ?? '',
        postal_code: formValues.postal_code ?? '',
        profile_picture: formValues.org_logo || formValues.image || undefined,
      };
    } else if (accountType === 'user') {
      payload = {
        full_name: formValues.name ?? '',
        email: formValues.email ?? '',
        primary_phone: formValues.mobile ?? '',
        mobile_number: formValues.mobile ?? '',
        address: formValues.address ?? '',
        state: formValues.state ?? '',
        city: formValues.city ?? '',
        district: formValues.district ?? '',
        country: formValues.country ?? '',
        postal_code: formValues.postal_code ?? '',
        profile_picture: formValues.org_logo || formValues.image || undefined,
      };
    } else if (accountType === 'branch') {
      payload = {
        branch_name: formValues.name ?? '',
        contact_email: formValues.email ?? '',
        contact_phone: formValues.mobile ?? '',
        address: formValues.address ?? '',
        state: formValues.state ?? '',
        city: formValues.city ?? '',
        district: formValues.district ?? '',
        country: formValues.country ?? '',
        postal_code: formValues.postal_code ?? '',
        branch_logo: formValues.org_logo || formValues.image || undefined,
      };
    } else {
      payload = {
        primary_contact_name: formValues.name ?? '',
        organization_name: formValues.name ?? '',
        primary_contact_email: formValues.email ?? '',
        contact_email: formValues.email ?? '',
        primary_contact_phone: formValues.mobile ?? '',
        contact_phone: formValues.mobile ?? '',
        address: formValues.address ?? '',
        state: formValues.state ?? '',
        city: formValues.city ?? '',
        district: formValues.district ?? '',
        country: formValues.country ?? '',
        postal_code: formValues.postal_code ?? '',
        org_logo: formValues.org_logo || formValues.image || undefined,
      };
    }

    this.apiService.updateUser(profileId, payload).subscribe({
      next: (response: any) => {
        this.toastrMsgService.success(this.translate.instant('PROFILE_TOAST_UPDATED_SUCCESS'));

        const updatedData = response?.data || response;
        if (updatedData?.org_logo) {
          this.imageurl = this.imageBaseUrl + updatedData.org_logo;
        }

        if (updatedData) {
          this.applyProfilePayload({ ...this.profileData, ...updatedData });
          const storedUser = localStorage.getItem('user');
          if (storedUser) {
            try {
              const parsed = JSON.parse(storedUser);
              const updatedUser = {
                ...parsed,
                user: {
                  ...(parsed?.user || {}),
                  ...updatedData,
                },
              };
              localStorage.setItem('user', JSON.stringify(updatedUser));
            } catch (storageError) {
              console.error('Failed to update localStorage user', storageError);
            }
          }
        }

        this.file = null;
      },
      error: (error) => {
        console.error('Error updating profile:', error);
        this.toastrMsgService.error(error.error?.message || this.translate.instant('PROFILE_ERR_UPDATE_FAILED'));
      },
      complete: () => {
        console.log('Update request completed');
      },
    });
  }

  saveProfile() {
    this.updateAdmin();
  }

  onStateChange(): void {
    const state = this.myForm.get('state')?.value;
    this.myForm.patchValue({ district: '', city: '' });
    this.districtOptions = [];
    this.cityOptions = [];
    if (!state) {
      return;
    }
    this.apiService.getdistricts(state).subscribe({
      next: (res) => {
        this.districtOptions = this.asLocArray(res);
      },
      error: (err) => {
        console.error(err);
        this.toastrMsgService.error(
          err.error?.errorMessage || err.error?.message || this.translate.instant('PROFILE_ERR_LOAD_DISTRICTS'),
        );
      },
    });
  }

  onDistrictChange(): void {
    const state = this.myForm.get('state')?.value;
    const district = this.myForm.get('district')?.value;
    this.myForm.patchValue({ city: '' });
    this.cityOptions = [];
    if (!state || !district) {
      return;
    }
    this.apiService.getSubdistricts(state, district).subscribe({
      next: (res) => {
        this.cityOptions = this.asLocArray(res);
      },
      error: (err) => {
        console.error(err);
        this.toastrMsgService.error(
          err.error?.errorMessage || err.error?.message || this.translate.instant('PROFILE_ERR_LOAD_CITIES'),
        );
      },
    });
  }

  setFormState(value: boolean) {
    if (this.profileData?.role?.type == 'admin') {
      this.myForm.enable();
      return;
    }
    if (value == true) {
      this.myForm.enable();
    } else {
      this.myForm.disable();
    }
  }
}
